import dot from 'dot-object'
import { useFormikContext } from 'formik'
import { useRouter } from 'next/router'
import RCSteps, { Step as RCStep } from 'rc-steps'
import { createContext, useCallback, useContext, useMemo } from 'react'
import { MdCheck } from 'react-icons/md'

import { Divider } from 'src/components/atoms/Divider'
import { SitePhotosBySiteId } from 'src/components/organisms/forms/ApplicationForm/types'

import { S3Asset } from 'src/types/form'
import { parseStepIndex, setActiveStepIndex } from 'src/utils/stepper'

import { NotificationType, notify } from '../NotificationProvider'
import { Step } from './Step'

export type StepType = {
  name: string
  component: () => JSX.Element
  validationSchema?: any // yup schema
  s3Assets?: S3Asset[] | SitePhotosBySiteId
}

export type StepperContextProps = {
  step: StepType
  steps: StepType[]
  stepIndex: number
  /**
   * Advances stepper to the next step
   * @param shouldRevalidate Set to false if the step was manually validated already using validateStep or validateIndexedStep
   * @returns
   */
  nextStep: (shouldRevalidate?: boolean) => Promise<void>
  validateStep: () => Promise<boolean>
  /**
   * Validates an array (indexed) value of a form
   * @param propertyName The property name of the form values to validate
   * @param index The index of the value to validate
   * @returns True if the values at the index has no validation errors
   */
  validateIndexedStep: (propertyName: string, index: number) => Promise<boolean>
  onSuccess?: (...args: any) => void
  onReset?: () => void
}

const StepperContext = createContext<StepperContextProps>(
  {} as StepperContextProps
)

export const StepperProvider = ({
  steps,
  showStepSelector = true,
  onSuccess,
  onReset,
  variant = 'selector',
  showDivider = true
}: {
  steps: StepType[]
  showStepSelector?: boolean
  onSuccess?: (...args: any) => void
  onReset: () => void
  variant?: 'selector' | 'text'
  showDivider?: boolean
}): JSX.Element => {
  const router = useRouter()
  const { validateForm, setFieldTouched } = useFormikContext()

  const stepIndex = useMemo(
    () =>
      parseStepIndex(
        steps.length,
        router.query.stepIndex as string | undefined
      ),
    [router.query.stepIndex, steps.length]
  )

  const step = steps[stepIndex]!

  const validateStep = useCallback(
    async (detailedErrors: boolean = false) => {
      const errors = await validateForm()
      const errorKeys = Object.keys(dot.dot(errors))

      if (errorKeys.length) {
        errorKeys.map((key: string) => setFieldTouched(key, true, true))
        notify({
          message: `You have one or more mandatory fields not filled in or checked. ${
            detailedErrors ? JSON.stringify(errors) : ''
          }`,
          type: NotificationType.error
        })
        return false
      }
      return true
    },
    [setFieldTouched, validateForm]
  )

  const validateIndexedStep = useCallback(
    async (propertyName: string, index: number) => {
      const errors = (await validateForm()) as any
      const errorKeys = Object.keys((errors[propertyName] || {})[index] || {})

      if (errorKeys.length) {
        errorKeys.map((key: string) =>
          setFieldTouched(`${propertyName}.${index}.${key}`, true, true)
        )
        notify({
          message:
            'You have one or more mandatory fields not filled in or checked.',
          type: NotificationType.error
        })
        return false
      }
      return true
    },
    [setFieldTouched, validateForm]
  )

  const nextStep = useCallback(
    async (shouldValidate = true) => {
      if (!shouldValidate) {
        setActiveStepIndex(router, steps.length, stepIndex + 1)
        return
      }

      if (await validateStep()) {
        setActiveStepIndex(router, steps.length, stepIndex + 1)
      }
    },
    [router, stepIndex, steps.length, validateStep]
  )

  const memoisedValue = useMemo(
    () => ({
      step,
      steps,
      stepIndex,
      nextStep,
      validateStep,
      validateIndexedStep,
      onSuccess,
      onReset
    }),
    [
      step,
      steps,
      stepIndex,
      nextStep,
      validateStep,
      validateIndexedStep,
      onSuccess,
      onReset
    ]
  )

  const stepsContent = useMemo(() => {
    if (!showStepSelector) {
      return null
    }
    if (variant === 'text') {
      return (
        <span className='text-steelGrey text-[14px] leading-[20px] font-medium'>{`Step ${
          steps.indexOf(step) + 1
        } of ${steps.length}`}</span>
      )
    }
    return (
      <>
        <RCSteps
          className='pointer-events-none'
          current={steps.indexOf(step)}
          icons={{
            finish: <MdCheck />,
            error: null
          }}
          labelPlacement='vertical'
        >
          {steps.map(s => (
            <RCStep key={s.name} title={s.name} />
          ))}
        </RCSteps>
        {showDivider ? <Divider /> : <div className='h-0 mb-5 md:mb-10' />}
      </>
    )
  }, [showDivider, showStepSelector, step, steps, variant])

  return (
    <StepperContext.Provider value={memoisedValue}>
      {showStepSelector ? stepsContent : null}
      <Step />
    </StepperContext.Provider>
  )
}

export const useStepper = () => useContext(StepperContext)
